import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { TEXT_BLOCK_DESCRIPTION_LIMIT } from "entities/modules/constants";
import { ModuleContentBlocksContent } from "entities/modules/types";
import { useGeneralData } from "modules/moduleTemplates/components/ModuleDetailsDrawer/stores/useGeneralData";
import { SyntheticEvent, useEffect, useState, type FC } from "react";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import { SelectOption } from "shared/components/Form/Select";
import TextField from "shared/components/Form/TextField";
import UploadImageField from "shared/components/Form/UploadImageField";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import { CTADestinationOptions } from "shared/constants/singleCardModule";
import { COLOR_BLACK } from "shared/theme/colors";
import {
  StyledContainer,
  StyledFormLabel,
  StyledRowContainer,
  StyledSelect,
} from "./styled";

type Props = {
  item: ModuleContentBlocksContent;
  index: number;
  isLoadingFile: boolean;
};

const OneCardBlockForm: FC<Props> = ({ item, index, isLoadingFile }) => {
  const [gamesList, setGamesList] = useState<SelectOption[]>([]);

  const {
    contentBlocks,
    oneCardBlockImage,
    setOneCardBlockImage,
    setContentBlocks,
  } = useGeneralData();

  const { searchData, isLoading, tableData } = useGamesData({
    isActive: [],
    size: 10,
  });

  useEffect(() => {
    const formattedGames = tableData?.data.map((game) => ({
      value: game.id.toString(),
      label: game.displayName ?? "",
    }));
    setGamesList(formattedGames);
  }, [isLoading, tableData]);

  const onSearch = (value: string) => {
    searchData.onChange(value);
  };

  if (!contentBlocks) {
    return null;
  }

  const onChange = (
    value: string | boolean | SelectOption | File | null,
    field: string,
  ) => {
    const newContent = contentBlocks.map((item, i) =>
      index === i ? { ...item, [field]: value } : item,
    );

    setContentBlocks(newContent);
  };

  const onChangeImage = (file: File | null) => {
    setOneCardBlockImage([file]);
  };

  const handleTypeChange = (_: SyntheticEvent, game: SelectOption) => {
    onChange(game, "selectedGame");
  };

  const handleChangeUrlOpenSettings = (value: string) => {
    if (value === "sameTab") {
      onChange(false, "urlOpensInNewTab");
      return;
    }
    if (value === "newTab") {
      onChange(true, "urlOpensInNewTab");
      return;
    }
  };
  return (
    <StyledContainer>
      <TextField
        id={"title"}
        label="Title*"
        placeholder="Click here to edit text"
        fullWidth
        required
        value={item.title}
        onChange={(event) => onChange(event.target.value, "title")}
        labelColor={COLOR_BLACK}
      />
      <TextField
        id={"description"}
        label="Description*"
        placeholder="Click here to edit text"
        fullWidth
        required
        multiline
        value={item.description}
        onChange={(event) => onChange(event.target.value, "description")}
        charactersLimit={TEXT_BLOCK_DESCRIPTION_LIMIT}
        InputProps={{ minRows: 5 }}
        labelColor={COLOR_BLACK}
      />
      <UploadImageField
        imageFile={oneCardBlockImage[index]}
        setImageFile={onChangeImage}
        label="Upload image*"
        isLoading={isLoadingFile}
        index={index}
      />
      <StyledRowContainer>
        <TextField
          id={"cta-for-registered-user"}
          label="CTA for registered user*"
          placeholder="Click here to edit text"
          fullWidth
          required
          value={item.ctaForRegisteredUser}
          onChange={(event) =>
            onChange(event.target.value, "ctaForRegisteredUser")
          }
          labelColor={COLOR_BLACK}
        />
        <TextField
          id="cta-for-unregistered-user"
          label="CTA for unregistered user*"
          placeholder="Click here to edit text"
          fullWidth
          required
          value={item.ctaForUnRegisteredUser}
          onChange={(event) =>
            onChange(event.target.value, "ctaForUnRegisteredUser")
          }
          labelColor={COLOR_BLACK}
        />
      </StyledRowContainer>
      <div>
        <FieldLabel label="CTA Destination" labelColor={COLOR_BLACK} />
        <StyledSelect
          value={item.ctaDestination ?? undefined}
          options={CTADestinationOptions}
          handleChange={(value) => onChange(value, "ctaDestination")}
        />
      </div>
      {item.ctaDestination === "game" && (
        <AutocompleteField
          disableCloseOnSelect
          id="games"
          defaultValue={
            contentBlocks?.find((content) => content.selectedGame)?.selectedGame
          }
          options={gamesList}
          onChange={handleTypeChange}
          loading={isLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <>
              <FieldLabel label="Select game" labelColor={COLOR_BLACK} />
              <TextField
                {...params}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={"Select game"}
              />
            </>
          )}
        />
      )}
      {item.ctaDestination === "url" && (
        <>
          <TextField
            id={"url"}
            label="URL*"
            placeholder="Click here to edit text"
            fullWidth
            required
            value={item.url}
            onChange={(event) => onChange(event.target.value, "url")}
            labelColor={COLOR_BLACK}
          />
          <FieldLabel label="URL opens in" labelColor={COLOR_BLACK} />
          <RadioGroup
            name="url-destination"
            defaultValue={item.urlOpensInNewTab ? "newTab" : "sameTab"}
            onChange={(event) =>
              handleChangeUrlOpenSettings(event.target.value)
            }
          >
            <StyledFormLabel
              value="sameTab"
              control={<Radio />}
              label="Same tab"
            />
            <StyledFormLabel
              value="newTab"
              control={<Radio />}
              label="New tab"
            />
          </RadioGroup>
        </>
      )}
    </StyledContainer>
  );
};

export default OneCardBlockForm;
